import { TopPage } from "@/components/elements/TopPage";
import { getDynamicRoute } from "@/utils/url";
import { Stack, styled } from "@mui/material";
import { FC, useMemo, useState, useEffect } from "react";
import { matchRoutes, Outlet, useLocation, useParams } from "react-router-dom";
import { AppRoutes } from "@/routes/config";
import { getPartnerMenu } from "@/features/rso/constants/partnerMenu";
import { TPartnerMenu } from "@/features/rso/types/partnerMenu";
import { GenericTabs } from "@/components/commons/GenericTabs";
import { blue } from "@/theme/colors";
import { InnerBox } from "@/components/elements/InnerBox";
import { useCountContractStatusCount } from "@/api/services/admin/rso";

type TLayoutProps = {};

export const Layout: FC<TLayoutProps> = () => {
  const { id } = useParams();
  const { pathname } = useLocation();

  const { data: contractStatusCount } = useCountContractStatusCount();

  const partnerMenu = useMemo(() => getPartnerMenu(contractStatusCount?.data), [contractStatusCount]);

  const activeIndex = partnerMenu.findIndex(
    ({ url, extraUrls }: TPartnerMenu) =>
      matchRoutes([{ path: url }], pathname) !== null || extraUrls?.some((extraUrl: string) => matchRoutes([{ path: extraUrl }], pathname) !== null),
  );
  const [value, setValue] = useState(activeIndex !== -1 ? activeIndex : 0);

  useEffect(() => {
    if (activeIndex !== -1 && activeIndex !== value) {
      setValue(activeIndex);
    }
  }, [activeIndex, pathname]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const isExtraUrlActive = useMemo(() => {
    return partnerMenu.some(({ extraUrls }: TPartnerMenu) =>
      extraUrls?.some((extraUrl: string) => matchRoutes([{ path: extraUrl }], pathname) !== null),
    );
  }, [pathname, partnerMenu]);

  const backUrl = useMemo(() => {
    if (isExtraUrlActive) {
      const activeMenu = partnerMenu.find(({ extraUrls }: TPartnerMenu) =>
        extraUrls?.some((extraUrl: string) => matchRoutes([{ path: extraUrl }], pathname) !== null),
      );
      return activeMenu ? getDynamicRoute(activeMenu.url, { id }) : AppRoutes.duringContractList;
    }
    return AppRoutes.duringContractList;
  }, [isExtraUrlActive, pathname, id, partnerMenu]);

  return (
    <Stack gap={2.5} sx={{ mt: 1 }}>
      {isExtraUrlActive ? (
        <TopPage
          title="登録支援機関の一覧"
          description="システムに登録されている登録支援機関の一覧です。新規登録する場合は「新規顧客登録」で追加した後に顧客ユーザーを登録してください。"
          backUrl={backUrl}
        />
      ) : (
        <TopPage
          title="登録支援機関の一覧"
          description="システムに登録されている登録支援機関の一覧です。新規登録する場合は「新規顧客登録」で追加した後に顧客ユーザーを登録してください。"
          enablePageBack={false}
        />
      )}
      <GenericTabs
        colorBorder={blue[80]}
        tabs={partnerMenu.map(({ title, url }: TPartnerMenu) => ({ label: title, url }))}
        value={value}
        onChange={handleChange}
        id={id}
      />
      <OutletBox>
        <Outlet />
      </OutletBox>
    </Stack>
  );
};

const OutletBox = styled(InnerBox)`
  margin-top: -20px;
  padding-top: 8px;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
`;
